import { Add, TrashOutlined } from 'assets/icons/revamp';
import BaseButton from 'components/ui/BaseButton';
import COLOR from 'constants/color';
import { ChoiceType } from 'interfaces/common.interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { updateMoreOptionChoices } from 'store/reducer/modal/slice';
import { isActivityRandomChoiceValid } from 'utils/activity-choices.util';
import RandomChoiceForm from './random-choicec-form';
import { moreOptionChoiceItemRandomChoicesSelector } from 'store/reducer/modal/more-options.modal.selectors';
import { DEFAULT_CHOICE_ITEM } from 'assets/data';
import { EMPTY_STRING } from 'constants/general';
import {
  applyTimerModeToChoice,
  getTimerModeToggleResult
} from 'utils/habit-validation.util';

interface RandomChoicesState {
  selectedChoiceId: string | null;
  useTimerByChoiceId: Record<string, boolean>;
}

interface CachedChoiceTimerToggleParams {
  readonly choice: ChoiceType;
  readonly nextUseTimer: boolean;
  readonly activityType: string;
  readonly completionById: Record<string, string>;
  readonly durationById: Record<string, number>;
}

const getCachedChoiceTimerToggleResult = ({
  choice,
  nextUseTimer,
  activityType,
  completionById,
  durationById
}: CachedChoiceTimerToggleParams) =>
  getTimerModeToggleResult({
    useTimer: nextUseTimer,
    durationSeconds: choice.duration_seconds,
    completionRequirements: choice.completion_requirements,
    cachedCompletion:
      completionById[choice.id] ??
      choice.completion_requirements ??
      EMPTY_STRING,
    cachedDuration: durationById[choice.id] ?? choice.duration_seconds ?? 0,
    activityType
  });

const RandomChoices = () => {
  const dispatch = useAppDispatch();
  const { activity, activityType } = useAppSelectorShallow(
    moreOptionChoiceItemRandomChoicesSelector
  );
  const choices = useMemo(() => activity?.choices ?? [], [activity?.choices]);
  const lastCompletionByChoiceId = useRef<Record<string, string>>({});
  const lastDurationByChoiceId = useRef<Record<string, number>>({});
  const [randomChoicesState, setRandomChoicesState] =
    useState<RandomChoicesState>({
      selectedChoiceId: choices[0]?.id ?? null,
      useTimerByChoiceId: {}
    });

  const currentChoice =
    choices.find(
      (choice) => choice.id === randomChoicesState.selectedChoiceId
    ) ?? null;
  const currentChoiceUsesTimer = currentChoice
    ? (randomChoicesState.useTimerByChoiceId[currentChoice.id] ??
      !currentChoice.completion_requirements)
    : true;

  useEffect(() => {
    if (choices.length === 0) {
      setRandomChoicesState((prevState) => ({
        ...prevState,
        selectedChoiceId: null
      }));
      return;
    }

    const selectedChoiceExists = choices.some(
      (choice) => choice.id === randomChoicesState.selectedChoiceId
    );
    if (!selectedChoiceExists) {
      setRandomChoicesState((prevState) => ({
        ...prevState,
        selectedChoiceId: choices[0].id
      }));
    }
  }, [choices, randomChoicesState.selectedChoiceId]);

  const handleAddChoice = () => {
    const newChoice = DEFAULT_CHOICE_ITEM();
    dispatch(updateMoreOptionChoices([...choices, newChoice]));
    setRandomChoicesState((prevState) => ({
      selectedChoiceId: newChoice.id,
      useTimerByChoiceId: {
        ...prevState.useTimerByChoiceId,
        [newChoice.id]: true
      }
    }));
  };

  const handleDeleteChoice = (choiceId: string) => {
    const deletedChoiceIndex = choices.findIndex(
      (choice) => choice.id === choiceId
    );
    const newChoices = choices.filter((choice) => choice.id !== choiceId);
    dispatch(updateMoreOptionChoices(newChoices));
    setRandomChoicesState((prevState) => {
      const { [choiceId]: _deletedChoice, ...nextUseTimerByChoiceId } =
        prevState.useTimerByChoiceId;
      const shouldSelectNext =
        prevState.selectedChoiceId === choiceId ||
        !newChoices.some((choice) => choice.id === prevState.selectedChoiceId);
      return {
        selectedChoiceId: shouldSelectNext
          ? (newChoices[deletedChoiceIndex]?.id ??
            newChoices[deletedChoiceIndex - 1]?.id ??
            null)
          : prevState.selectedChoiceId,
        useTimerByChoiceId: nextUseTimerByChoiceId
      };
    });
  };

  const handleChoiceChange = (
    choiceId: string,
    field: keyof ChoiceType,
    value: ChoiceType[keyof ChoiceType]
  ) => {
    const updatedChoices = choices.map((choice) =>
      choice.id === choiceId ? { ...choice, [field]: value } : choice
    );
    dispatch(updateMoreOptionChoices(updatedChoices));
  };

  const handleUseTimerChange = useCallback(
    (choiceId: string, nextUseTimer: boolean) => {
      const choice = choices.find((item) => item.id === choiceId);
      if (!choice) {
        return;
      }

      const result = getCachedChoiceTimerToggleResult({
        choice,
        nextUseTimer,
        activityType,
        completionById: lastCompletionByChoiceId.current,
        durationById: lastDurationByChoiceId.current
      });
      lastCompletionByChoiceId.current[choiceId] = result.cachedCompletion;
      lastDurationByChoiceId.current[choiceId] = result.cachedDuration;
      dispatch(
        updateMoreOptionChoices(
          choices.map((item) =>
            item.id === choiceId ? applyTimerModeToChoice(item, result) : item
          )
        )
      );
      setRandomChoicesState((prevState) => ({
        ...prevState,
        useTimerByChoiceId: {
          ...prevState.useTimerByChoiceId,
          [choiceId]: nextUseTimer
        }
      }));
    },
    [activityType, choices, dispatch]
  );

  const getChoiceItemStateClassName = (choice: ChoiceType) => {
    if (!isActivityRandomChoiceValid(choice)) {
      return 'border-red-400 dark:border-red-500 bg-red-50/50 dark:bg-red-900/10';
    }
    if (randomChoicesState.selectedChoiceId === choice.id) {
      return 'border-primary-500 bg-primary-400/20';
    }
    return 'border-secondary-400 dark:border-secondary-dark-400';
  };

  return (
    <div className='w-full flex flex-col ~gap-4/6'>
      {choices.length ? (
        <div className='flex flex-col ~gap-3/4'>
          <p className='~text-xs/sm text-gray-600 dark:text-gray-400'>
            {t('tabs_revamp.activity_choices_tab.count_choices_added', {
              count: choices.length
            })}
          </p>
          <div className='flex flex-col ~gap-2/3'>
            {choices.map((choice, index) => (
              <div
                key={choice.id}
                className={`flex items-center ~gap-2/3 px-2 py-1 rounded-md border ${getChoiceItemStateClassName(choice)}`}>
                <button
                  onClick={() =>
                    setRandomChoicesState((prevState) => ({
                      ...prevState,
                      selectedChoiceId: choice.id
                    }))
                  }
                  className='flex-1 text-left ~text-xs/sm font-medium text-black dark:text-white'>
                  {choice.name ||
                    t('tabs_revamp.activity_choices_tab.choice_number', {
                      index: index + 1
                    })}
                </button>
                <BaseButton
                  onClick={() => handleDeleteChoice(choice.id)}
                  icon={
                    <TrashOutlined
                      fill={COLOR.SEMANTIC.ERROR}
                      className='~w-4/5 ~h-4/5'
                    />
                  }
                  size='xs'
                  variant='icon'
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className='~text-xs/sm text-gray-600 dark:text-gray-400 m-auto'>
          {t('tabs_revamp.activity_choices_tab.no_choices_added')}
        </p>
      )}

      {currentChoice && (
        <RandomChoiceForm
          key={currentChoice.id}
          currentChoice={currentChoice}
          handleChoiceChange={(field, value) =>
            handleChoiceChange(currentChoice.id, field, value)
          }
          useTimer={currentChoiceUsesTimer}
          setUseTimer={(nextUseTimer) =>
            handleUseTimerChange(currentChoice.id, nextUseTimer)
          }
        />
      )}

      <div className='flex items-center justify-end ~gap-2/3'>
        <BaseButton
          title={t('new_choice')}
          onClick={handleAddChoice}
          size='xs'
          icon={
            <Add
              className='~w-3/4 ~h-3/4'
              fill={COLOR.PRIMARY.LIGHT_900}
              fillDarkMode={COLOR.PRIMARY.DARK_600}
            />
          }
          variant='secondary'
        />
      </div>
    </div>
  );
};

export default RandomChoices;
